import React, { useState, useEffect } from 'react';
import './NormalSudokuGame.css';
import NumberSelectionPopup from './NumberSelectionPopup';
import SavedGamePopup from './SavedGamePopup';
import {
  calculateCandidates,
  findSingleCandidateCells,
  findSinglePlacementCells,
  generateHint
} from '../utils/sudokuUtils';

const NormalSudokuGame = ({ 
  board, 
  originalBoard, 
  onCellInput, 
  isLoading, 
  onCheck 
}) => {
  const [selectedCell, setSelectedCell] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [highlightedNumbers, setHighlightedNumbers] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [currentHint, setCurrentHint] = useState(null);
  const [showAssistMode, setShowAssistMode] = useState(false);
  const [singleCandidateCells, setSingleCandidateCells] = useState([]);
  const [singlePlacementCells, setSinglePlacementCells] = useState([]);
  const [showSavePopup, setShowSavePopup] = useState(false);
  const [showLoadPopup, setShowLoadPopup] = useState(false);
  const [savedGames, setSavedGames] = useState([]);

  // 盤面が完全に埋まっているかチェック
  const isBoardFull = board && board.every(row => row.every(cell => cell !== 0));

  // アシストモードの効果を更新
  useEffect(() => {
    if (showAssistMode && board) {
      // 単一候補セルを検出
      const singleCells = findSingleCandidateCells(board);
      setSingleCandidateCells(singleCells);
      
      // 単一配置セルを検出
      const placementCells = findSinglePlacementCells(board);
      setSinglePlacementCells(placementCells);
    } else {
      setSingleCandidateCells([]);
      setSinglePlacementCells([]);
    }
  }, [board, showAssistMode]);
  
  // ローカルストレージから保存されたゲームをロード
  useEffect(() => {
    const storedGames = localStorage.getItem('sudokuSavedGames');
    if (storedGames) {
      setSavedGames(JSON.parse(storedGames));
    }
  }, []);

  // ヒントを表示する関数
  const handleShowHint = () => {
    if (!board) return;
    
    const hint = generateHint(board);
    setCurrentHint(hint);
    setShowHint(true);
  };

  // ヒント表示を閉じる
  const handleCloseHint = () => {
    setShowHint(false);
  };

  // アシストモードの切り替え
  const toggleAssistMode = () => {
    setShowAssistMode(!showAssistMode);
  };
  
  // 盤面を保存する関数
  const handleSaveGame = () => {
    setShowSavePopup(true);
  };
  
  // 保存ダイアログを閉じる
  const handleCloseSavePopup = () => {
    setShowSavePopup(false);
  };
  
  // 盤面を実際に保存する
  const saveGame = (slotName, memo = '') => {
    const gameData = {
      id: Date.now(),
      name: slotName || `保存データ ${savedGames.length + 1}`,
      date: new Date().toLocaleString(),
      board: board,
      memo: memo,
    };
    
    const updatedSavedGames = [...savedGames, gameData];
    setSavedGames(updatedSavedGames);
    localStorage.setItem('sudokuSavedGames', JSON.stringify(updatedSavedGames));
    setShowSavePopup(false);
  };
  
  // 保存された盤面を読み込む関数
  const handleLoadGame = () => {
    setShowLoadPopup(true);
  };
  
  // 読み込みダイアログを閉じる
  const handleCloseLoadPopup = () => {
    setShowLoadPopup(false);
  };
  
  // 保存データから盤面を読み込む
  const loadGame = (gameData) => {
    if (gameData && gameData.board) {
      onCellInput(null, null, null, gameData.board); // App.jsに盤面全体を更新させる
      setShowLoadPopup(false);
    }
  };
  
  // 保存データを削除する関数
  const deleteGame = (gameId) => {
    const filteredGames = savedGames.filter(game => game.id !== gameId);
    setSavedGames(filteredGames);
    localStorage.setItem('sudokuSavedGames', JSON.stringify(filteredGames));
  };

  // セルをクリックした時の処理
  const handleCellClick = (row, col) => {
    // 元々埋まっていたマスは選択できない
    if (originalBoard[row][col] !== 0) return;
    
    // セルを選択
    setSelectedCell({ row, col });
    
    // 選択したセルの位置を取得して吹き出しの位置を設定
    const cellElement = document.getElementById(`cell-${row}-${col}`);
    if (cellElement) {
      const rect = cellElement.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      // 画面幅を取得
      const windowWidth = window.innerWidth;
      
      // 右側3列（6, 7, 8）のセルが選択された場合、吹き出しを左側に表示
      const isRightSideCell = col >= 6;
      
      // 吹き出しの位置を設定
      if (isRightSideCell) {
        // 右側のセルの場合、左側に吹き出しを配置
        setPopupPosition({
          top: rect.top + scrollTop,
          left: rect.left + scrollLeft - 200 // 吹き出しの幅を考慮して左にオフセット
        });
      } else {
        // 左側または中央のセルの場合、右側に吹き出しを配置（従来通り）
        setPopupPosition({
          top: rect.top + scrollTop,
          left: rect.left + scrollLeft
        });
      }
      
      setShowPopup(true);
    }
  };

  // 数字パッドで数字を選択した時の処理
  const handleHighlightNumber = (number) => {
    setHighlightedNumbers(prev => {
      // 既に選択されている場合は削除、そうでなければ追加
      if (prev.includes(number)) {
        return prev.filter(n => n !== number);
      } else {
        return [...prev, number];
      }
    });
  };

  // 吹き出し表示から数字を選択した時の処理
  const handleNumberSelect = (number) => {
    if (selectedCell) {
      onCellInput(selectedCell.row, selectedCell.col, number);
      setShowPopup(false);
      setSelectedCell(null);
    }
  };

  // 吹き出しの外側をクリックしたら閉じる
  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedCell(null);
  };

  // セルの背景色を決定する関数
  const getCellBackground = (row, col, value) => {
    // アシストモード: 単一候補セル
    const isSingleCandidate = singleCandidateCells.some(
      cell => cell.row === row && cell.col === col
    );
    
    // アシストモード: 単一配置セル
    const isSinglePlacement = singlePlacementCells.some(
      cell => cell.row === row && cell.col === col
    );
    
    // アシストモードの特別なハイライト
    if (showAssistMode) {
      if (isSingleCandidate) {
        return 'rgba(0, 128, 255, 0.3)'; // 青色ハイライト（単一候補）
      }
      if (isSinglePlacement) {
        return 'rgba(255, 128, 0, 0.3)'; // オレンジ色ハイライト（単一配置）
      }
    }
    
    // 元々埋まっていたマスは灰色背景
    if (originalBoard[row][col] !== 0) {
      return 'var(--original-cell-bg)';
    }
    
    // ハイライト表示の場合
    if (highlightedNumbers.length > 0) {
      // 色のマップ（数字に対応）
      const colorMap = {
        1: 'rgba(66, 133, 244, 0.3)',  // 青
        2: 'rgba(219, 68, 55, 0.3)',   // 赤
        3: 'rgba(15, 157, 88, 0.3)',   // 緑
        4: 'rgba(244, 180, 0, 0.3)',   // 黄
        5: 'rgba(171, 71, 188, 0.3)',  // 紫
        6: 'rgba(255, 87, 34, 0.3)',   // オレンジ
        7: 'rgba(0, 172, 193, 0.3)',   // シアン
        8: 'rgba(121, 85, 72, 0.3)',   // 茶
        9: 'rgba(167, 155, 142, 0.3)'  // グレージュ
      };
      
      // このセルに該当する強調表示番号のリスト
      const cellHighlights = [];
      
      // セルの値が強調表示対象かチェック
      const isHighlightedValue = value !== 0 && highlightedNumbers.includes(value);
      if (isHighlightedValue) {
        cellHighlights.push({ type: 'value', number: value });
      }
      
      // 行、列、ボックスに強調表示対象があるかチェック
      for (const num of highlightedNumbers) {
        // 行チェック
        const inSameRow = board[row].includes(num);
        if (inSameRow) cellHighlights.push({ type: 'row', number: num });
        
        // 列チェック
        const inSameCol = board.some(r => r[col] === num);
        if (inSameCol) cellHighlights.push({ type: 'col', number: num });
        
        // ボックスチェック
        const boxRow = Math.floor(row / 3) * 3;
        const boxCol = Math.floor(col / 3) * 3;
        const inSameBox = board.some((r, i) => 
          i >= boxRow && i < boxRow + 3 && r.slice(boxCol, boxCol + 3).includes(num)
        );
        if (inSameBox) cellHighlights.push({ type: 'box', number: num });
      }
      
      // ハイライト対象がない場合
      if (cellHighlights.length === 0) {
        return '';
      }
      
      // セルの値自体が強調表示対象の場合、その色を優先
      if (isHighlightedValue) {
        return colorMap[value];
      }
      
      // 同じ数字の異なる領域（行、列、ボックス）が重なる場合は縞々にしない
      const uniqueNumbers = [...new Set(cellHighlights.map(h => h.number))];
      
      if (uniqueNumbers.length === 1) {
        // 同じ数字の行、列、ボックスが重なる場合は、通常のハイライト（縞々なし）
        return colorMap[uniqueNumbers[0]].replace('0.3', '0.15');
      } else {
        // 異なる数字の領域が重なる場合は縞々表示
        const gradientSteps = [];
        const stepSize = 10; // 縞の幅
        
        uniqueNumbers.forEach((num, index) => {
          const position = index * stepSize;
          gradientSteps.push(
            `${colorMap[num].replace('0.3', '0.15')} ${position}px`,
            `${colorMap[num].replace('0.3', '0.15')} ${position + stepSize}px`
          );
        });
        
        return `repeating-linear-gradient(45deg, ${gradientSteps.join(', ')})`;
      }
    }
    
    return '';
  };

  // セルのフォントスタイルを決定する関数
  const getCellFontStyle = (row, col, value) => {
    // ベーススタイル
    const baseStyle = originalBoard[row][col] !== 0 
      ? { fontWeight: 'bold', color: 'var(--original-cell-color)' }
      : { fontWeight: 'normal', color: 'var(--user-cell-color)' };
    
    // ハイライトされた数字は色を変えて下線を引く
    if (value !== 0 && highlightedNumbers.includes(value)) {
      // 数字ごとの色
      const colorMap = {
        1: '#4285f4', // 青
        2: '#db4437', // 赤
        3: '#0f9d58', // 緑
        4: '#f4b400', // 黄
        5: '#ab47bc', // 紫
        6: '#ff5722', // オレンジ
        7: '#00acc1', // シアン
        8: '#795548', // 茶
        9: '#a79b8e'  // グレージュ
      };
      
      return {
        ...baseStyle,
        color: colorMap[value],
        textDecoration: 'underline',
        fontWeight: 'bold'
      };
    }
    
    return baseStyle;
  };

  // 数字の出現回数を計算する関数
  const countDigitOccurrences = () => {
    if (!board) return Array(10).fill(0);
    
    // 0～9までの出現回数を格納する配列（0は空白マス）
    const counts = Array(10).fill(0);
    
    // 盤面を走査して数字をカウント
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        const digit = board[row][col];
        counts[digit]++;
      }
    }
    
    return counts;
  };
  
  // 数字の出現回数を取得
  const digitCounts = countDigitOccurrences();
  
  if (!board) return <div className="loading-board">読み込み中...</div>;

  return (
    <div className="normal-sudoku-game">
      {/* アシスト機能 */}
      <div className="assist-controls">
        <button 
          className={`assist-button ${showAssistMode ? 'active' : ''}`}
          onClick={toggleAssistMode}
        >
          アシストモード {showAssistMode ? 'ON' : 'OFF'}
        </button>
        <button className="hint-button" onClick={handleShowHint}>
          ヒントをください
        </button>
      </div>
      
      {/* 保存・読み込み機能 */}
      <div className="save-load-controls">
        <button className="save-button" onClick={handleSaveGame}>
          盤面を保存
        </button>
        <button className="load-button" onClick={handleLoadGame}>
          盤面を読み込む
        </button>
      </div>
      
      {/* 数字の出現回数表示 */}
      <div className="digit-counter">
        <div className="digit-counter-title">数字の出現回数</div>
        <div className="digit-counter-container">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
            <div 
              key={`counter-${digit}`} 
              className={`digit-counter-item ${highlightedNumbers.includes(digit) ? 'highlighted' : ''}`}
              onClick={() => handleHighlightNumber(digit)}
            >
              <div className="digit-counter-number">{digit}</div>
              <div className="digit-counter-count">{digitCounts[digit]}/9</div>
            </div>
          ))}
        </div>
      </div>
      
      {/* ヒント表示エリア */}
      {showHint && currentHint && (
        <div className="hint-container">
          <div className="hint-content">
            <p className="hint-message">{currentHint.message}</p>
            {currentHint.count > 0 && (
              <p className="hint-count">{currentHint.count}箇所見つかりました</p>
            )}
            <button className="hint-close" onClick={handleCloseHint}>閉じる</button>
          </div>
        </div>
      )}
      
      {/* 単一候補・単一配置の説明 */}
      {showAssistMode && (singleCandidateCells.length > 0 || singlePlacementCells.length > 0) && (
        <div className="assist-legend">
          {singleCandidateCells.length > 0 && (
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: 'rgba(0, 128, 255, 0.3)' }}></div>
              <div className="legend-text">単一候補: {singleCandidateCells.length}箇所</div>
            </div>
          )}
          {singlePlacementCells.length > 0 && (
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: 'rgba(255, 128, 0, 0.3)' }}></div>
              <div className="legend-text">単一配置: {singlePlacementCells.length}箇所</div>
            </div>
          )}
        </div>
      )}

      <div className="normal-sudoku-board">
        {board.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className="board-row">
            {row.map((cell, colIndex) => (
              <div
                id={`cell-${rowIndex}-${colIndex}`}
                key={`cell-${rowIndex}-${colIndex}`}
                className={`
                  board-cell
                  ${(rowIndex + 1) % 3 === 0 && rowIndex < 8 ? 'border-bottom' : ''}
                  ${(colIndex + 1) % 3 === 0 && colIndex < 8 ? 'border-right' : ''}
                  ${cell === 0 ? 'empty-cell' : 'filled-cell'}
                  ${originalBoard[rowIndex][colIndex] !== 0 ? 'original-cell' : 'input-cell'}
                  ${selectedCell && selectedCell.row === rowIndex && selectedCell.col === colIndex ? 'selected-cell' : ''}
                `}
                style={{ 
                  background: getCellBackground(rowIndex, colIndex, cell),
                }}
                onClick={() => !isLoading && handleCellClick(rowIndex, colIndex)}
              >
                <span style={getCellFontStyle(rowIndex, colIndex, cell)}>
                  {cell !== 0 ? cell : ''}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {/* 数字パッド */}
      <div className="number-pad">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(number => (
          <button
            key={`number-${number}`}
            className={`number-button ${highlightedNumbers.includes(number) ? 'active' : ''}`}
            onClick={() => handleHighlightNumber(number)}
            style={{
              backgroundColor: highlightedNumbers.includes(number) ? `var(--number-${number}-color)` : '',
              color: highlightedNumbers.includes(number) ? 'white' : ''
            }}
          >
            {number}
          </button>
        ))}
      </div>
      
      {/* 判定ボタン */}
      {isBoardFull && (
        <div className="check-button-container">
          <button className="check-button" onClick={onCheck}>
            判定する
          </button>
        </div>
      )}
      
      {/* 数字選択吹き出し */}
      {showPopup && selectedCell && (
        <NumberSelectionPopup
          position={popupPosition}
          onSelect={handleNumberSelect}
          onClose={handleClosePopup}
        />
      )}
      
      {/* 保存ポップアップ */}
      {showSavePopup && (
        <SavedGamePopup
          type="save"
          onClose={handleCloseSavePopup}
          onSave={saveGame}
          savedGames={savedGames}
        />
      )}
      
      {/* 読み込みポップアップ */}
      {showLoadPopup && (
        <SavedGamePopup
          type="load"
          onClose={handleCloseLoadPopup}
          onLoad={loadGame}
          onDelete={deleteGame}
          savedGames={savedGames}
        />
      )}
    </div>
  );
};

export default NormalSudokuGame;